import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import { ABOUT_SOLUTION_CARDS_BY_LOCALE } from '@/data/shared/aboutCopy';

import Area from '@/components/apps/layouts/shared/Area';
import { AniSlideUp } from '@/components/shared/animations/basic';

import { trackAboutSolutionSelection } from './aboutAnalytics';

/**
 * ### AboutWhatWeDoSection
 *
 * The two products, one panel each: what it is for, the single figure that
 * stands for it, and a way through to its own page. The whole panel is the
 * link, so the figure and the name are the thing being clicked rather than a
 * button parked underneath them.
 *
 * The About page says only this much about the products on purpose. The
 * solution pages are where the features and the cases live, and repeating
 * them here left the company itself unsaid.
 *
 * @returns The rendered section element
 * @see {@link ABOUT_SOLUTION_CARDS_BY_LOCALE} The copy rendered here
 */
export default function AboutWhatWeDoSection() {
  const { t } = useTranslation('common');
  const currentLang = useCurrentLang();
  const cards = ABOUT_SOLUTION_CARDS_BY_LOCALE[currentLang];

  return (
    /* 위 AboutVisual(#161617) 레이어가 남아 비쳐 보이지 않도록 자체 배경을 칠한다 */
    <section
      className="relative z-[1] bg-apps-coreGrayWhite"
      data-analytics-section="about_what_we_do"
    >
      <Area>
        <div className="flex flex-col gap-12 py-[clamp(100px,10.417vw,180px)] lg:gap-16">
          <div className="max-w-[820px]">
            <p className="text-[13px] font-bold uppercase leading-[140%] tracking-[0.65px] text-apps-corePrimary01 lg:text-[15px]">
              What We Do
            </p>
            <h2 className="mt-4 text-balance text-[30px] font-bold leading-[134%] tracking-[-0.6px] text-apps-coreGrayBlack sm:text-[36px] lg:mt-6 lg:text-[clamp(42px,3.611vw,56px)] lg:leading-[126%] lg:tracking-[-1.12px]">
              {t('세상을 바꾸는 디플리 사운드 AI 기술')}
            </h2>
          </div>
          <AniSlideUp>
            <ul className="grid gap-4 md:grid-cols-2 lg:gap-6">
              {cards.map((card) => (
                <li key={card.id}>
                  <Link
                    href={card.href}
                    prefetch={false}
                    onClick={() => trackAboutSolutionSelection(card.id)}
                    className="group flex h-full flex-col justify-between gap-12 rounded-3xl border border-apps-coreGray06 p-8 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-apps-coreGray04 hover:shadow-[0_24px_60px_-30px_rgba(22,22,23,0.45)] lg:gap-16 lg:p-12"
                  >
                    <div>
                      <h3 className="text-[26px] font-bold leading-[132%] tracking-[-0.52px] text-apps-coreGrayBlack lg:text-[clamp(28px,2.361vw,34px)]">
                        {card.name}
                      </h3>
                      <p className="mt-4 text-pretty text-[17px] font-medium leading-[160%] tracking-[-0.34px] text-apps-coreGray01 lg:mt-5 lg:text-[19px]">
                        {card.summary}
                      </p>
                    </div>
                    <div>
                      <p className="bg-[linear-gradient(99deg,#a11034_0%,#e31b42_60%,#ff3d59_100%)] bg-clip-text font-dmSans text-[56px] font-bold leading-[104%] tracking-[-1.12px] [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] [font-variant-numeric:tabular-nums] lg:text-[clamp(64px,5.556vw,80px)] lg:tracking-[-1.6px]">
                        {card.figure}
                      </p>
                      <p className="mt-3 text-pretty text-[14px] font-medium leading-[150%] tracking-[-0.28px] text-apps-coreGray02 lg:text-[15px]">
                        {card.figureCaption}
                      </p>
                      <span className="mt-8 inline-flex items-center gap-2 text-[15px] font-bold leading-[140%] tracking-[-0.3px] text-apps-coreGrayBlack lg:mt-10 lg:text-[16px]">
                        {t('자세히 보기')}
                        <svg
                          viewBox="0 0 20 20"
                          className="h-4 w-4 transition-transform duration-300 ease-out group-hover:translate-x-1"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.8}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M4 10h11M11 5.5 15.5 10 11 14.5" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </AniSlideUp>
        </div>
      </Area>
    </section>
  );
}
